import React, { useEffect, useState } from "react";
import { loadStyles } from "../../../utils/cssLoader";
import { getData } from "../../../utils/getData";
import { getImageUrl } from "../../../utils/generalLiveCenter";

/**
 * LeagueTable — modern, self-contained football league-standings widget.
 *
 * Two variants (both render this component):
 *   • detailed-league-table → every group's full standings (all rows, all columns)
 *   • preview-league-table  → the first group's top 5 rows only
 *
 * Data: /league/{id}/table?type=list → an ARRAY of groups, each:
 *   { nm, fid, football_tournament_tables:[ { plc, clr, team:{id,nm,sn}, pld, w, d, l, scr, con, dif, pts } ] }
 * A single-table league returns one group; a cup returns many (e.g. World Cup → 12).
 * No Home/Away split (deliberately omitted). Colours are CMS-token driven.
 *
 * Data source (priority): serverData prop → hydration DOM (data-listingdata) → self-fetch.
 */

// Normalise any wrapper into an array of group objects, or null.
const groupsFromRaw = (raw) => {
  if (!raw || typeof raw !== "object") return null;
  let o = raw;
  for (let i = 0; i < 4; i++) {
    if (Array.isArray(o)) break;
    if (o?.football_tournament_tables) {
      o = [o]; // single-object (non-list) shape → wrap as one group
      break;
    }
    o = o?.data;
    if (o === undefined || o === null) return null;
  }
  if (!Array.isArray(o)) return null;
  const groups = o.filter((g) => g && Array.isArray(g.football_tournament_tables));
  return groups.length ? groups : null;
};

const numDiff = (n) => {
  const v = Number(n);
  if (Number.isNaN(v)) return n ?? "";
  return v > 0 ? `+${v}` : `${v}`;
};

// Form is not in the /league/{id}/table payload today, but render it if it
// ever appears (frm / form / recent_form as an array of "W"/"D"/"L").
const rowForm = (r) => {
  const f = r?.frm || r?.form || r?.recent_form;
  return Array.isArray(f) ? f.slice(-5) : null;
};

// "World Cup Group A" → "World Cup" (for the header when there are many groups).
const competitionName = (name) => {
  const stripped = String(name || "").replace(/\s+Group\s+.*$/i, "").trim();
  return stripped || name || "League Table";
};

const Crest = ({ src, name }) => (
  <span className="lt__crest">
    {src ? (
      <img
        src={src}
        alt={name ? `${name} crest` : ""}
        loading="lazy"
        onError={(e) => {
          e.currentTarget.style.display = "none";
        }}
      />
    ) : null}
  </span>
);

const FormDots = ({ form }) => (
  <span className="lt__form">
    {form.map((r, i) => {
      const k = String(r || "").toUpperCase().charAt(0);
      const cls = k === "W" ? "win" : k === "L" ? "loss" : "draw";
      return (
        <span key={i} className={`lt__fdot lt__fdot--${cls}`}>
          {k}
        </span>
      );
    })}
  </span>
);

// One group's table. `offset`/`limit` render a page slice (used by the preview pager).
const GroupTable = ({ group, sport, offset = 0, limit = Infinity, subhead = false }) => {
  const allRows = [...(group.football_tournament_tables || [])].sort(
    (a, b) => (a?.plc ?? 0) - (b?.plc ?? 0)
  );
  const rows = allRows.slice(offset, offset + limit);
  if (!rows.length) return null;
  const hasForm = allRows.some((r) => rowForm(r));

  return (
    <div className="lt__grp">
      {subhead ? <div className="lt__group">{group.nm}</div> : null}
      <div className="lt__scroll">
        <table className="lt__table">
          <thead>
            <tr>
              <th className="lt__c-rank">#</th>
              <th className="lt__c-team">Team</th>
              <th className="lt__c-num">P</th>
              <th className="lt__c-num">W</th>
              <th className="lt__c-num">D</th>
              <th className="lt__c-num">L</th>
              <th className="lt__c-num lt__c-ga">F</th>
              <th className="lt__c-num lt__c-ga">A</th>
              <th className="lt__c-num lt__c-diff">GD</th>
              <th className="lt__c-pts">PTS</th>
              {hasForm ? <th className="lt__c-form">Form</th> : null}
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => {
              const team = r?.team || {};
              const name = team.nm || team.sn || "—";
              const code = team.code || team.sn || null;
              const crest = team.id ? getImageUrl("", "", sport, team.id) : null;
              const form = rowForm(r);
              return (
                <tr key={r?.team_id ?? i} className="lt__row">
                  <td className="lt__c-rank">
                    <span className="lt__rankno">{r?.plc ?? i + 1}</span>
                  </td>
                  <td className="lt__c-team">
                    <span className="lt__team">
                      <Crest src={crest} name={name} />
                      <span className="lt__tmeta">
                        <span className="lt__tname">{name}</span>
                        {code ? <span className="lt__tcode">{code}</span> : null}
                      </span>
                    </span>
                  </td>
                  <td className="lt__c-num">{r?.pld}</td>
                  <td className="lt__c-num">{r?.w}</td>
                  <td className="lt__c-num">{r?.d}</td>
                  <td className="lt__c-num">{r?.l}</td>
                  <td className="lt__c-num lt__c-ga">{r?.scr}</td>
                  <td className="lt__c-num lt__c-ga">{r?.con}</td>
                  <td className="lt__c-num lt__c-diff">{numDiff(r?.dif)}</td>
                  <td className="lt__c-pts">{r?.pts}</td>
                  {hasForm ? (
                    <td className="lt__c-form">{form ? <FormDots form={form} /> : null}</td>
                  ) : null}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const LeagueTable = ({
  variant = "detailed",
  dataId,
  baseUrl,
  sport = "football",
  serverData = null,
  data: dataProp = null,
  dataClient = false,
  loadCSS = true,
}) => {
  const isPreview = variant === "preview";

  useEffect(() => {
    loadStyles("leagueTableWidget");
  }, []);

  const [groups, setGroups] = useState(groupsFromRaw(serverData || dataProp));
  const [page, setPage] = useState(0); // preview pagination

  useEffect(() => {
    const g = groupsFromRaw(serverData || dataProp);
    if (g) setGroups(g);
  }, [serverData, dataProp]);

  // Hydration: re-read the serialized payload off the container.
  useEffect(() => {
    if (!dataClient) return;
    try {
      const el = document.querySelector(".microservice-cont");
      const parsed = JSON.parse(el?.getAttribute("data-listingdata") || "null");
      const g = groupsFromRaw(parsed?.data);
      if (g) setGroups(g);
    } catch (_) {}
  }, [dataClient]);

  // Self-fetch fallback — runs whenever we still have no groups (embed mode, OR hydration mode
  // when the serialized data-listingdata read didn't yield a table). NOT gated on dataClient, so
  // a failed/empty hydration payload can never leave the widget stuck on "Loading…".
  useEffect(() => {
    if (groups || !dataId || !baseUrl) return;
    let cancelled = false;
    getData({
      dataId,
      dataType: "detailed-league-table",
      baseUrl,
      sport,
    }).then((res) => {
      if (cancelled) return;
      const g = groupsFromRaw(res);
      if (g) setGroups(g);
    });
    return () => {
      cancelled = true;
    };
  }, [groups, dataId, baseUrl, sport]);

  if (!groups) {
    return (
      <div className="lt lt--loading">
        <span>Loading table…</span>
      </div>
    );
  }

  const multi = groups.length > 1;
  const title = multi
    ? competitionName(groups[0]?.nm)
    : groups[0]?.nm || "League Table";

  const fid = groups[0]?.fid;
  const flag = fid ? getImageUrl(baseUrl, "league", sport, fid) : null;

  // Preview pagination. Multi-group competitions (e.g. World Cup) page by GROUP —
  // one group per page; a single-table league pages by ROWS — 5 per page.
  const PAGE = 5;
  const previewMulti = isPreview && multi;
  const rowTotal = groups[0]?.football_tournament_tables?.length || 0;
  const totalPages = previewMulti
    ? groups.length
    : Math.max(1, Math.ceil(rowTotal / PAGE));
  const pg = Math.min(page, totalPages - 1);
  const start = pg * PAGE;
  const end = Math.min(start + PAGE, rowTotal);
  const rangeLabel = previewMulti
    ? `Group ${pg + 1} of ${groups.length}`
    : rowTotal > PAGE
    ? `Showing ${start + 1}–${end} of ${rowTotal}`
    : null;

  return (
    <div className={`lt lt--${variant}`}>
      {/* Header — title left, page range right (preview) */}
      <div className="lt__head">
        <span className="lt__head-l">
          {flag ? (
            <span className="lt__logo">
              <img
                src={flag}
                alt=""
                onError={(e) => {
                  e.currentTarget.parentElement.style.display = "none";
                }}
              />
            </span>
          ) : null}
          <h3 className="lt__title">{title}</h3>
        </span>
        {isPreview && rangeLabel ? (
          <span className="lt__range">{rangeLabel}</span>
        ) : null}
      </div>

      {isPreview ? (
        <>
          {previewMulti ? (
            <GroupTable group={groups[pg]} sport={sport} subhead />
          ) : (
            <GroupTable group={groups[0]} sport={sport} offset={start} limit={PAGE} />
          )}
          {totalPages > 1 ? (
            <div className="lt__pager">
              <button
                type="button"
                className="lt__pg-arrow"
                disabled={pg === 0}
                onClick={() => setPage(pg - 1)}
                aria-label="Previous"
              >
                ‹
              </button>
              {Array.from({ length: totalPages }).map((_, i) => (
                <button
                  type="button"
                  key={i}
                  className={`lt__pg${i === pg ? " lt__pg--on" : ""}`}
                  onClick={() => setPage(i)}
                >
                  {i + 1}
                </button>
              ))}
              <button
                type="button"
                className="lt__pg-arrow"
                disabled={pg === totalPages - 1}
                onClick={() => setPage(pg + 1)}
                aria-label="Next"
              >
                ›
              </button>
            </div>
          ) : null}
        </>
      ) : (
        groups.map((g, i) => (
          <GroupTable key={g.id ?? i} group={g} sport={sport} subhead={multi} />
        ))
      )}
    </div>
  );
};

export default LeagueTable;
